/**
 * Provision assist chat pane — INSTANCE_PROVISION (deploy + setup in one conversation).
 */

import React, { useMemo } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { ConversationShell } from '@/components/ai-studio/conversation';
import type { ActiveConversationContext } from '@/types/aiStudio';
import type { InstanceProvisionPatch } from '@/types/aiStudioAgent';
import {
  ASSIST_PANE_HEADER_CLASS,
  ASSIST_PANE_TITLE_CLASS,
} from '@/components/ai-studio/assist/assistPaneStyles';
import type { useProvisionAssist } from './useProvisionAssist';

const PROVISION_HINTS = [
  'Chicago plant, 12 injection presses',
  'Deploy under North America / Manufacturing',
  'What should my scheduling upload include?',
];

interface ProvisionAssistPanelProps {
  scopeLabel: string;
  assist: ReturnType<typeof useProvisionAssist>;
  onApplyPatch: (patch: InstanceProvisionPatch) => void;
}

export const ProvisionAssistPanel: React.FC<ProvisionAssistPanelProps> = ({
  scopeLabel,
  assist,
  onApplyPatch,
}) => {
  const { t } = useLanguage();

  const context: ActiveConversationContext = useMemo(
    () => ({
      contextType: 'INSTANCE_PROVISION',
      scopeLabel,
      status: 'open',
    }),
    [scopeLabel]
  );

  const handleAccept = () => {
    if (!assist.pendingSuggestion) return;
    onApplyPatch(assist.pendingSuggestion);
    assist.clearSuggestion();
  };

  const chatShell = assist.loading ? (
    <p className="px-3 py-2 text-base text-[var(--color-surface-text-secondary)]">
      {t('aiStudio.instances.loading')}
    </p>
  ) : (
    <ConversationShell
      className="min-h-0 flex-1"
      context={context}
      messages={assist.messages}
      sending={assist.sending}
      error={assist.errorKey ? t(assist.errorKey) : null}
      onSend={(text) => void assist.send(text)}
      hideContextIndicator
      paneMode
      composerVariant="pill"
      composerFloating
      composerHints={PROVISION_HINTS}
      composerHintStepMs={5000}
      composerPlaceholder={t('aiStudio.provision.placeholder')}
      composerFooter={
        assist.pendingSuggestion ? (
          <button
            type="button"
            onClick={handleAccept}
            className="w-full rounded-full bg-[var(--color-accent-bg)] px-4 py-2 text-base font-medium text-[var(--color-accent-text)] hover:opacity-90"
          >
            {t('aiStudio.provision.acceptSuggestion')}
          </button>
        ) : null
      }
    />
  );

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <header className={ASSIST_PANE_HEADER_CLASS}>
        <h2 className={ASSIST_PANE_TITLE_CLASS}>{t('aiStudio.provision.title')}</h2>
      </header>
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-[var(--color-secondary-bg)]">
        {chatShell}
      </div>
    </div>
  );
};
